import { createContext, use, type ReactNode } from 'react';
import type { WizardReducer } from './reducer/wizardReducer';
import type {
	FullOrderViewModel,
	GoalAndInterventionDefinitionLibraryViewModel,
	GoalDefinitionViewModel,
	GoalsAndInterventionsViewModel,
	GoalViewModel,
	InterventionViewModel
} from '@generated';
import type { SuggestedGoal } from '../suggestions';

export interface GoalsAndInterventionsWizardContextProps extends WizardReducer {
	fullOrder: FullOrderViewModel;
	goalsAndInterventions: GoalsAndInterventionsViewModel;
	suggestedGoalsAndInterventions: SuggestedGoal[];
	goalAndInterventionDefinitionLibrary: GoalAndInterventionDefinitionLibraryViewModel;
	defaultAssignedDisciplineId?: number;
	children: ReactNode;
}

export interface GoalsAndInterventionsWizardContextData extends WizardReducer {
	certificationPeriod: { startDate: Date; endDate: Date };
	existingGoals: GoalViewModel[];
	existingInterventions: InterventionViewModel[];
	suggestedGoalsAndInterventions: SuggestedGoal[];
	goalAndInterventionDefinitionLibrary: GoalAndInterventionDefinitionLibraryViewModel;
	dispatchPredefinedGoalSelected: (goalDefinition: GoalDefinitionViewModel) => void;
	defaultAssignedDisciplineId?: number;
}

const GoalsAndInterventionsWizardContext = createContext<GoalsAndInterventionsWizardContextData | null>(null);

export function GoalsAndInterventionsWizardContextProvider({ children, ...props }: GoalsAndInterventionsWizardContextProps) {
	const dispatchPredefinedGoalSelected = (goalDefinition: GoalDefinitionViewModel) => {
		const goalDefinitionProceduredIs = goalDefinition.goalProcedures.map(x => x.procedureConceptId);
		props.dispatch({
			type: 'PREDEFINED_GOAL_SELECTED',
			goalDefinition: goalDefinition,
			searchTerm: '',
			newGoalId: window.crypto.randomUUID(),
			existingInterventions: [...props.goalsAndInterventions.interventions, ...props.fullOrder.interventions],
			goalInterventionDefinitions: props.goalAndInterventionDefinitionLibrary.interventionDefinitions.filter(
				x => x.procedureConceptId && goalDefinitionProceduredIs.includes(x.procedureConceptId)
			)
		});
	};
	const existingGoals = [...props.fullOrder.goals, ...props.goalsAndInterventions.goals];
	const existingInterventions = [...props.fullOrder.interventions, ...props.goalsAndInterventions.interventions];
	return (
		<GoalsAndInterventionsWizardContext
			value={{
				...props,
				dispatchPredefinedGoalSelected,
				certificationPeriod: props.fullOrder.certificationPeriod,
				existingGoals,
				existingInterventions
			}}
		>
			{children}
		</GoalsAndInterventionsWizardContext>
	);
}

export function useGoalsAndInterventionsWizard() {
	const context = use(GoalsAndInterventionsWizardContext);
	if (!context) {
		throw new Error('useGoalsAndInterventionsWizard must be used within a GoalsAndInterventionsWizardContextProvider');
	}
	return context;
}
